import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import React, { createRef, forwardRef, JSX, Ref, RefObject, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import dayjs from 'dayjs';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/date-range-input.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-input.en.t.json.keys';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { TFunction } from 'react-i18next';
import { CustomDisplayProps } from './CustomDateDisplay';

export type OnChangeDate = Date | null | [Date | null, Date | null];
export type OnChangeDateIntersection = Date & null & [Date | null, Date | null];

interface KeyboardInputConfig {
  translateFn: TFunction<string, string>;
  inputValue: string;
  format: string | undefined;
  minDate: Date | null | undefined;
  maxDate: Date | null | undefined;
}

interface KeyboardValidationResult {
  message: string;
  date: Date | undefined;
}

const validateKeyboardInput = (config: KeyboardInputConfig): KeyboardValidationResult => {
  let message = '';
  let date: Date | undefined = undefined;

  const dayJsParsed = dayjs.utc(config.inputValue, config.format, true);

  // first check if entered date is valid to the given format
  if (!dayJsParsed.isValid()) {
    message = config.translateFn(
      config.format ? Keys.invalidDateWithFormat : Keys.invalidDateFormat,
      {
        format: config.format
      }
    );
  } else {
    date = dayJsParsed.toDate();
  }

  // validate minDate if set
  if (config.minDate && dayjs(config.minDate).isAfter(dayJsParsed)) {
    message = config.translateFn(Keys.invalidMinDate, {
      minDate: dayjs(config.minDate).format(config.format)
    });
    date = undefined;
  }

  // validate maxDate if set
  if (config.maxDate && dayjs(config.maxDate).isBefore(dayJsParsed)) {
    message = config.translateFn(Keys.invalidMaxDate, {
      maxDate: dayjs(config.maxDate).format(config.format)
    });
    date = undefined;
  }

  return { message, date };
};

export const DateWithKeyboardSupportInner = (
  props: CustomDisplayProps & DataTestIdProp,
  ref: Ref<Button>
): JSX.Element => {
  const {
    onClick,
    onInputChange,
    date,
    format,
    placeholder,
    dataTestId,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    setValueToNullWhenFormatIsIncorrect,
    noInitialValueInput
  } = props;
  const inputRef: RefObject<HTMLInputElement> = createRef();

  const [isInitialized, setIsInitialized] = useState(noInitialValueInput ? false : true);

  useEffect(() => {
    if (isInitialized && date) {
      if (inputRef && inputRef.current) {
        inputRef.current.value = dayjs(date).format(format);
      }
    }
  }, [date, isInitialized]);

  const [errorMsg, setErrorMsg] = useState('');

  const { t } = useTranslation('date-range-input', fallback, Keys);

  return (
    <div data-testid={dataTestId}>
      <div className="d-flex">
        <div className="mr-2 flex-column flex-grow-1">
          <input
            type="text"
            className={`iso__date-picker__input  ${inputClassName ? inputClassName : ''}`}
            placeholder={placeholder}
            defaultValue={noInitialValueInput ? '' : date ? dayjs(date).format(format) : ''}
            ref={inputRef}
            onBlur={val => {
              setIsInitialized(true);
              if (!val.target.value || val.target.value.trim() === '') {
                return;
              }

              if (!!onInputChange && setValueToNullWhenFormatIsIncorrect) {
                const parsedDayJs = dayjs.utc(val.target.value, format, true);
                if (!parsedDayJs.isValid()) {
                  onInputChange(null);
                }
              }

              // using form validation (DatePickerWrapper & FormDatePicker) will skip DatePicker's own validation
              if (!!onInputChange && useFormValidation) {
                const parsedDayJs = dayjs.utc(val.target.value, format, true);
                if (parsedDayJs.isValid()) {
                  onInputChange(parsedDayJs.toDate() as OnChangeDate);
                }
                return;
              }

              const checkInputValidity = validateKeyboardInput({
                translateFn: t,
                inputValue: val.target.value,
                format,
                minDate,
                maxDate
              });

              if (!!onInputChange && checkInputValidity.message === '' && checkInputValidity.date) {
                setErrorMsg('');
                onInputChange(checkInputValidity.date as OnChangeDate);
              }

              if (checkInputValidity.message !== '') {
                setErrorMsg(checkInputValidity.message);
              }
            }}
          />
        </div>
        <Button
          onClick={() => {
            setIsInitialized(true);
            if (onClick) {
              onClick();
            }
          }}
          ref={ref}
          className="iso__date-picker__keyboardSupportButton"
        >
          <FontAwesomeIcon icon={faCalendarAlt} />
        </Button>
      </div>
      {!useFormValidation && <span className="validation-error">{errorMsg}</span>}
    </div>
  );
};

export const DateWithKeyboardSupport = forwardRef(DateWithKeyboardSupportInner) as (
  props: CustomDisplayProps & DataTestIdProp,
  ref: Ref<Button>
) => ReturnType<typeof DateWithKeyboardSupportInner>;
